import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { TResponse } from "../../../types/common";
import { IAdminUser, IAdminInvitePayload, IAdminUpdatePayload } from "../../../types/api/adminUser";
import { listAdminUsers, inviteAdmin, updateAdmin, deactivateAdmin } from "./callbacks";

export const useAdminUsers = () =>
  useQuery<TResponse<IAdminUser[]>, Error>({
    queryKey: [API_ENDPOINTS.ADMIN_USER_LIST],
    queryFn: () => listAdminUsers().then((r) => r.data),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });

export const useInviteAdmin = (options?: {
  onSuccess?: (d: IAdminUser) => void;
  onError?: (e: unknown) => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation<TResponse<IAdminUser>, Error, IAdminInvitePayload>({
    mutationFn: (data) => inviteAdmin(data).then((r) => r.data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.ADMIN_USER_LIST] });
      options?.onSuccess?.(res.data as IAdminUser);
    },
    onError: options?.onError,
  });
};

export const useUpdateAdmin = (options?: {
  onSuccess?: (d: IAdminUser) => void;
  onError?: (e: unknown) => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation<TResponse<IAdminUser>, Error, IAdminUpdatePayload>({
    mutationFn: (data) => updateAdmin(data).then((r) => r.data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.ADMIN_USER_LIST] });
      options?.onSuccess?.(res.data as IAdminUser);
    },
    onError: options?.onError,
  });
};

export const useDeactivateAdmin = (options?: {
  onSuccess?: () => void;
  onError?: (e: unknown) => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation<TResponse<null>, Error, number>({
    mutationFn: (id) => deactivateAdmin(id).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.ADMIN_USER_LIST] });
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
};
